import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigation, RefreshCw, Save, Loader2, Plus, Trash2, GripVertical, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function NavEditorPanel({ projectId, currentPageSlug, onEdit }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` };
  const pageSlug = currentPageSlug || 'home';

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/magic/projects/${projectId}/nav-read`, {
        params: { page_slug: pageSlug },
        headers: authHeader,
      });
      setLinks(res.data.links || []);
    } catch {
      toast.error('Failed to load nav links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
  }, [projectId, pageSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLink = (idx, field, value) => {
    setLinks(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const addLink = () => {
    setLinks(prev => [...prev, { label: 'New Link', href: '#', target: '_self' }]);
  };

  const removeLink = (idx) => {
    setLinks(prev => prev.filter((_, i) => i !== idx));
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = links.slice();
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setLinks(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/magic/projects/${projectId}/nav-edit`, {
        page_slug: pageSlug,
        links,
      }, { headers: authHeader });
      if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
      toast.success('Navigation updated across all pages');
    } catch {
      toast.error('Failed to save navigation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-3.5 h-3.5 text-[var(--orbis-t3)]" />
          <span className="text-xs text-[var(--orbis-t2)] font-medium">Navigation Links</span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1 text-[var(--orbis-t4)] hover:text-[var(--orbis-t1)] transition-colors"
          title="Reload from page"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-[var(--orbis-t4)]" />
        </div>
      ) : (
        <div className="space-y-2">
          {links.length === 0 && (
            <p className="text-[11px] text-[var(--orbis-t4)] text-center py-3">No nav links found</p>
          )}

          {links.map((link, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`bg-[var(--orbis-s1)] border rounded-md p-2 space-y-1.5 cursor-default ${dragIdx === idx ? 'opacity-40 border-[var(--orbis-amber)]/40' : 'border-[rgba(255,255,255,0.12)]'}`}
            >
              <div className="flex items-center gap-1.5">
                <GripVertical className="w-3 h-3 text-[var(--orbis-t4)] shrink-0 cursor-grab" />
                <input
                  type="text"
                  value={link.label}
                  onChange={e => updateLink(idx, 'label', e.target.value)}
                  placeholder="Link label"
                  className="flex-1 bg-transparent text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none min-w-0"
                />
                <button onClick={() => removeLink(idx)}
                  className="p-0.5 text-[var(--orbis-t4)] hover:text-red-400 transition-colors shrink-0">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={link.href}
                  onChange={e => updateLink(idx, 'href', e.target.value)}
                  placeholder="https:// or /page or #section"
                  className="flex-1 bg-[var(--orbis-s2)] border border-[rgba(255,255,255,0.08)] rounded px-2 py-1 text-[11px] text-[var(--orbis-t3)] placeholder:text-[var(--orbis-t4)] focus:outline-none min-w-0 font-mono"
                />
                <button
                  onClick={() => updateLink(idx, 'target', link.target === '_blank' ? '_self' : '_blank')}
                  title={link.target === '_blank' ? 'Opens in new tab' : 'Opens in same tab'}
                  className={`p-1 rounded transition-colors shrink-0 ${link.target === '_blank' ? 'text-[var(--orbis-amber)]' : 'text-[var(--orbis-t4)]'}`}
                >
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addLink}
            className="w-full flex items-center justify-center gap-1.5 border border-dashed border-[rgba(255,255,255,0.15)] rounded-md py-2 text-[11px] text-[var(--orbis-t4)] hover:text-[var(--orbis-t2)] hover:border-[rgba(255,255,255,0.25)] transition-colors"
          >
            <Plus className="w-3 h-3" /> Add link
          </button>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || links.length === 0}
            className="w-full bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] text-xs"
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Saving...</>
            ) : (
              <><Save className="w-3.5 h-3.5 mr-1.5" /> Update navigation</>
            )}
          </Button>

          <p className="text-[10px] text-[var(--orbis-t4)] text-center">
            Changes apply to all pages
          </p>
        </div>
      )}
    </div>
  );
}
